import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Building2, X, Upload, ArrowRight } from 'lucide-react';
import BillingProfileSetup from './BillingProfileSetup';

const KEY = (uid) => `operio-logo-prompt-seen-${uid}`;

const FirstTimeLogoPrompt = () => {
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user?.id || user.role !== 'admin') return;
    if (localStorage.getItem(KEY(user.id))) return;

    // Vänta ut onboarding-tour först (~3 sek efter dashboard mount)
    const t = setTimeout(async () => {
      try {
        const res = await api.getBillingCompleteness();
        if (!res.data.has_logo || !res.data.complete) {
          setShow(true);
        } else {
          localStorage.setItem(KEY(user.id), '1');
        }
      } catch { /* ignore */ }
    }, 3500);
    return () => clearTimeout(t);
  }, [user]);

  const dismiss = () => {
    if (user?.id) localStorage.setItem(KEY(user.id), '1');
    setShow(false);
  };

  if (!show && !showSetup) return null;

  if (showSetup) {
    return <BillingProfileSetup onClose={() => { setShowSetup(false); dismiss(); }} onSaved={() => { setShowSetup(false); dismiss(); }} />;
  }

  return (
    <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm flex items-center justify-center px-4" data-testid="first-time-logo-prompt">
      <div className="bg-card rounded-2xl shadow-2xl border border-border max-w-md w-full overflow-hidden">
        <div className="bg-gradient-to-br from-[#E8613A] to-[#D4532F] text-white p-6 relative">
          <button onClick={dismiss} className="absolute top-3 right-3 hover:bg-white/15 rounded p-1" data-testid="logo-prompt-close">
            <X size={16} />
          </button>
          <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mb-3">
            <Building2 size={26} />
          </div>
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Komplett dina företagsuppgifter
          </h2>
          <p className="text-sm text-white/85 mt-1">
            Innan du börjar skicka fakturor — lägg till logga, organisationsnummer och bankgiro så
            ser dina fakturor professionella ut.
          </p>
        </div>
        <div className="p-6 space-y-3">
          <ul className="space-y-2 text-sm">
            <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 bg-[#E8613A] rounded-full mt-2"></span> Ladda upp din företagslogga (visas överst på fakturor)</li>
            <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 bg-[#E8613A] rounded-full mt-2"></span> Fyll i org.nummer + adress</li>
            <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 bg-[#E8613A] rounded-full mt-2"></span> Lägg in bankgiro för betalningar</li>
          </ul>
          <div className="flex gap-2 mt-5">
            <Button variant="outline" onClick={dismiss} className="flex-1 rounded-full" data-testid="logo-prompt-later">
              Senare
            </Button>
            <Button onClick={() => { setShow(false); setShowSetup(true); }} className="flex-1 bg-[#E8613A] hover:bg-[#D4532F] text-white rounded-full" data-testid="logo-prompt-start">
              <Upload size={14} className="mr-1.5" /> Sätt upp nu <ArrowRight size={14} className="ml-1.5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FirstTimeLogoPrompt;
